import { Button } from "@progress/kendo-react-buttons";
import { Field, FormRenderProps } from "@progress/kendo-react-form";
import {
  NumericTextBox,
  NumericTextBoxBlurEvent,
  NumericTextBoxChangeEvent,
} from "@progress/kendo-react-inputs";
import { Error, Label } from "@progress/kendo-react-labels";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { CurveSelectionDialog } from "..";
import useTransactionDirections from "../../../hooks/useTransactionDirections";
import { useUserContext } from "../../../hooks/useUserContext";
import DateUtility from "../../../utilities/dateUtilities";
import { STRIKE_TYPES } from "../../../utilities/vanilla/vanillaData";
import { SettlementType, StrikeType, Structure } from "../../../utilities/vanilla/vanillaEnums";
import { requiredValidator } from "../../../validators";
import { FormInput } from "../../form";
import { FormChipList } from "../../form/FormChipList";
import { ICommodity } from "../Commodity";

interface Props {
  formRenderProps: FormRenderProps;
  allocations: any[];
  calculator: any;
  commodity: ICommodity;
  product: any;
  readOnly?: boolean;
  onLoaded?: () => void;
}

const VanillaStructure = ({
  formRenderProps,
  allocations,
  calculator,
  commodity,
  product,
  readOnly,
  onLoaded,
}: Props) => {
  const { instrument, company } = product;
  const units = commodity?.units;

  const { context: user } = useUserContext();

  const isFixed = formRenderProps.valueGetter("strikeType") === "Fixed";

  const decimals = commodity?.decimals || 2;
  const format = `c${decimals}`;
  const strikeType = isFixed ? StrikeType.Fixed : StrikeType.Adder;

  useEffect(() => {
    calculator.update();
  }, [isFixed]);

  const commonStrike = formRenderProps.valueGetter("commonStrike");

  useEffect(() => {
    calculator.setIsCommonStrike(commonStrike);
  }, [commonStrike]);

  useEffect(() => {
    if (allocations.length) {
      const formAllocationIndex = allocations.findIndex(
        (a) => a.allocationId === formRenderProps.valueGetter("allocationId"),
      );
      setAllocationIndex(formAllocationIndex);
    }
  }, [allocations.length]);

  const [allocationIndex, setAllocationIndex] = useState(-1);

  const [showCurveDialog, setShowCurveDialog] = useState(false);

  const onApplyAllocations = useCallback(
    (index: number) => {
      setShowCurveDialog(false);
      setAllocationIndex(index);
      const { values, allocationId } = allocations[index];

      calculator.setAllocation(values);
      formRenderProps.onChange("allocationId", { value: allocationId });
    },
    [allocations],
  );

  const handleChangeVolume = ({ target: { value, name } }: NumericTextBoxBlurEvent) => {
    if (!value) {
      value = 0;
    }
    const num = name?.split("_")[1] as string;
    const index = parseInt(num);

    calculator.setVolume(value, index);
    setAllocationIndex(-1);
    formRenderProps.onChange("allocationId", { value: null });
  };

  const settlementType = formRenderProps.valueGetter("settlement");
  let isDaily = settlementType === SettlementType.Daily,
    totalWidget;

  useEffect(() => {
    calculator.setIsDaily(isDaily);
  }, [isDaily]);

  const handleChangeTotalVolume = ({ target: { value } }: NumericTextBoxChangeEvent) => {
    if (value === null) return;
    calculator.setTotalVolume(value);
    if (allocationIndex === -1) {
      const flatAllocationIndex = allocations.findIndex(
        (allocation: any) => allocation.allocationId === 1,
      );
      setAllocationIndex(flatAllocationIndex);
      formRenderProps.onChange("allocationId", { value: 1 });
    }
  };

  const fieldStyle = { margin: 0 };

  const dollarsPerUnit = `($/${units.slice(0, -1)})`;

  const handleChangeStrike =
    (columnIndex: number, strikeType: StrikeType, month: number | null = null) =>
    ({ target: { value } }: NumericTextBoxChangeEvent) => {
      if (value === null) return;
      calculator.setStrike(value, columnIndex, strikeType, month);
    };

  const [allocatedData, setAllocatedData] = useState<any>([]);

  useEffect(() => {
    if (calculator) {
      const newAllocations = allocations.map((allocation: any) => {
        const { values } = allocation;
        const data = calculator.allocateValues(values);
        return [{ name: "Volume", data }];
      });

      if (JSON.stringify(newAllocations) !== JSON.stringify(allocatedData)) {
        setAllocatedData(newAllocations);
        onLoaded && onLoaded();
      }
    }
  }, [calculator.totalVolume, allocations, showCurveDialog]);

  let monthLabels = calculator.months.map((month: Date) => DateUtility.formatDateMY(month));
  if (isDaily) {
    monthLabels = monthLabels.map((month: string, i: number) => {
      return `${month} (${calculator.businessDays[i]})`;
    });
    totalWidget = (
      <Label style={{ marginTop: 20 }}>Total Business Days: {calculator.totalBusinessDays}</Label>
    );
  }

  useEffect(() => {
    formRenderProps.onChange("totalVolume", { value: calculator.totalVolume });
  }, []);

  useEffect(() => {
    formRenderProps.onChange("totalVolume", { value: calculator.totalVolume });
  }, [calculator.totalVolume]);

  useEffect(() => {
    formRenderProps.onChange("errors", { value: calculator.errors.length ? 0 : 1 });
  }, [calculator.errors]);

  const showAdders = strikeType === StrikeType.Adder;

  const structureId = formRenderProps.valueGetter("structureId");

  const isSwap = structureId === Structure.Swap;

  const strikeTypeFieldStyle = isSwap ? { visibility: "hidden", display: "none" } : { margin: 0 };

  const strikeTypeLabel = isSwap ? "" : "Strikes";

  const canCompanySell =
    company.features?.canSell ||
    (user.activeCompany.id === company.id && (user.roles.isBroker || user.roles.isRiskTaker));

  const transactionDirections = useTransactionDirections(canCompanySell, instrument.isSellable);

  const isMonthlyDisabled = commonStrike;
  const showMonthlyAdders = showAdders && !commonStrike;

  const columnComponents = calculator.columns.map((column: any, columnIndex: number) => {
    const monthlyValues = showMonthlyAdders ? column.adders : column.fixedStrikes;
    const avgValue = showAdders ? column.avgAdder : column.avgFixedStrike;
    const nextColumnIsLinked = calculator.columns[columnIndex + 1]?.isLinked;
    const isDisabled = column.isLinked && !nextColumnIsLinked;
    return (
      <Fragment key={columnIndex}>
        <GridLayoutItem col={3 + columnIndex} row={1}>
          <Label className="align-items-center" style={fieldStyle}>
            {column.label} {dollarsPerUnit}
          </Label>
        </GridLayoutItem>
        <GridLayoutItem col={3 + columnIndex} row={2}>
          <NumericTextBox
            spinners={false}
            format={format}
            onChange={handleChangeStrike(columnIndex, strikeType)}
            value={avgValue}
            style={fieldStyle}
            disabled={isDisabled || readOnly}
          />
        </GridLayoutItem>
        {monthlyValues.map((value: any, monthIndex: number) => {
          return (
            <GridLayoutItem col={3 + columnIndex} row={6 + monthIndex} key={monthIndex}>
              <NumericTextBox
                spinners={false}
                format={format}
                onChange={handleChangeStrike(columnIndex, strikeType, monthIndex)}
                value={value}
                style={fieldStyle}
                disabled={isDisabled || isMonthlyDisabled || readOnly}
              />
            </GridLayoutItem>
          );
        })}
      </Fragment>
    );
  });

  const curveCategories = useMemo(
    () => calculator.months.map((m: any) => ({ date: m })),
    [calculator.months],
  );

  const curveOptions = useMemo(
    () => allocations.map((a: any, i: number) => ({ label: a.name, value: i })),
    [allocations],
  );

  const handleCurveCancel = useCallback(() => setShowCurveDialog(false), []);

  return (
    <>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: 120,
            marginRight: 20,
          }}
        >
          <Field
            name="strikeType"
            label={strikeTypeLabel}
            component={FormChipList}
            data={STRIKE_TYPES}
            selection="single"
            style={strikeTypeFieldStyle}
            disabled={readOnly}
          />
          <Field
            name="direction"
            label="Direction"
            component={FormChipList}
            selection="single"
            validator={requiredValidator}
            {...transactionDirections}
            disabled={readOnly}
          />
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: 120,
            marginRight: 20,
          }}
        >
          <>
            <Label style={{ marginTop: 20 }}>Allocations</Label>
            <div>
              <Button
                size="small"
                disabled={calculator.totalVolume === 0 || readOnly}
                icon="align-self-end-alt"
                onClick={(e: any) => {
                  e.preventDefault();
                  setShowCurveDialog(true);
                }}
                themeColor="primary"
              />
            </div>
          </>
          {totalWidget}
        </div>
        <div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <GridLayout
              align={{ vertical: "middle" }}
              gap={{ rows: 0, cols: 10 }}
              style={{ marginTop: 16 }}
            >
              <GridLayoutItem col={1} row={3}>
                <Label>&nbsp;</Label>
              </GridLayoutItem>
              {monthLabels.map((label: string, i: number) => (
                <GridLayoutItem col={1} row={i + 6} key={i}>
                  <span className="k-font-weight-bold">{label}</span>
                </GridLayoutItem>
              ))}
              <GridLayoutItem col={2} row={1}>
                <Label style={fieldStyle}>Volume ({units})</Label>
              </GridLayoutItem>
              <GridLayoutItem col={2} row={2}>
                <NumericTextBox
                  spinners={false}
                  format="n0"
                  min={0}
                  onChange={handleChangeTotalVolume}
                  value={calculator.totalVolume}
                  style={fieldStyle}
                  disabled={readOnly}
                />
              </GridLayoutItem>
              {calculator.volumes.map((volume: number, i: number) => {
                const name = `volume_${i}`;
                return (
                  <GridLayoutItem col={2} row={i + 6} key={i}>
                    <NumericTextBox
                      name={name}
                      spinners={false}
                      format="n0"
                      onChange={handleChangeVolume}
                      value={volume}
                      min={0}
                      disabled={readOnly}
                    />
                  </GridLayoutItem>
                );
              })}

              {!isSwap && columnComponents}
            </GridLayout>

            <div style={{ display: "flex", alignSelf: "center" }}>
              <ul>
                {calculator.errors.map((error: string, i: number) => {
                  return (
                    <li key={i}>
                      <Error>{error}</Error>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>

        {showCurveDialog && allocations.length && (
          <CurveSelectionDialog
            title="Allocations"
            chartData={allocatedData}
            selection={allocationIndex}
            categories={curveCategories}
            units={units}
            options={curveOptions}
            onCancel={handleCurveCancel}
            onApply={onApplyAllocations}
          />
        )}
      </div>

      <Field
        name="totalVolume"
        component={FormInput}
        validator={requiredValidator}
        showValidationMessage={false}
        style={{ visibility: "hidden", display: "none" }}
      />

      <Field
        name="errors"
        component={FormInput}
        validator={requiredValidator}
        showValidationMessage={false}
        style={{ visibility: "hidden", display: "none" }}
      />
    </>
  );
};

export default VanillaStructure;
